import _sortBy from 'lodash.sortby';
import ActionModel from 'cio/models/action';
import { ConsolidatedActionsGroup, Edge } from 'cio/models/campaign';
import { SerializedLegacyAttributeFilter } from 'cio/types/conditions/legacy-filter';
import { getLocaleByCode } from 'cio/utils/languages';

// Process all edges and actions, consolidating edges together along
// the way if there are any multi_lang_branch_action objects
export function consolidateTree(edges: Edge[], actions: ActionModel[]) {
  let actionMap: Record<string, ActionModel> = {};
  let outEdges: Record<string, Edge[]> = {};
  let targetIds: Record<string, string[]> = {};

  let consolidatedActionMap: Record<string, ActionModel> = {}; // actions not in actionMap
  let hasConsolidatedActions = false;
  let consolidatedEdges = edges; // all edges after consolidation
  let consolidatedActions = actions; // all actions after consolidation
  let consolidatedActionsGroups: Record<string, ConsolidatedActionsGroup> = {}; // consolidated branch action groups
  let consolidatedBranchDefaultActions = new Set<string>(); // the actions that represent a consolidated group of actions on the VWB
  let parentConsolidatedBranchMap: Record<string, string> = {}; // branched actions and their consolidated branch action

  edges.forEach((e) => {
    if (outEdges[e.from] == null) {
      outEdges[e.from] = [];
    }

    outEdges[e.from] = _sortBy([...outEdges[e.from], e], ['index']);
  });

  actions.forEach((a) => {
    if (a.type === 'multi_lang_branch_action') {
      hasConsolidatedActions = true;

      let branchedActionIds: string[] = [];
      let branchedEdges: Edge[] = [];

      outEdges[a.id].forEach((edge) => {
        branchedActionIds[edge.index!] = edge.to;
        branchedEdges.push(edge);
        parentConsolidatedBranchMap[edge.to] = edge.from;
      });

      consolidatedActionsGroups[a.id] = {
        parentActionId: a.id,
        defaultActionId: branchedActionIds[branchedActionIds.length - 1],
        branchedActionIds: new Set(branchedActionIds),
        edges: branchedEdges,
        name: '',
        actionLanguageMap: {},
      };

      consolidatedActionMap[a.id] = a;
    } else {
      actionMap[a.id] = a;

      if (targetIds[a.id] == null) {
        targetIds[a.id] = [];
      }

      if (outEdges[a.id] != null) {
        targetIds[a.id].push(...outEdges[a.id].map((e) => e.to));
      }
    }
  });

  if (hasConsolidatedActions) {
    consolidatedEdges = [];
    consolidatedActions = [];

    consolidatedBranchDefaultActions = new Set(
      Object.values(consolidatedActionsGroups).map((group) => group.defaultActionId),
    );

    // ensure parsed target IDs don't reference consolidated/hidden actions
    Object.keys(targetIds).forEach((key) => {
      targetIds[key] = targetIds[key].map((target) => {
        let consolidatedGroup = consolidatedActionsGroups[target];
        if (consolidatedGroup != null) {
          if (target !== consolidatedGroup.defaultActionId) {
            return consolidatedGroup.defaultActionId;
          }
        }

        return target;
      });
    });

    consolidatedActions = actions.filter(
      (a) =>
        consolidatedActionsGroups[a.id] == null &&
        (parentConsolidatedBranchMap[a.id] == null || consolidatedBranchDefaultActions.has(a.id)),
    );

    edges.forEach((edge) => {
      if (parentConsolidatedBranchMap[edge.from] != null && consolidatedActionsGroups[edge.to]) {
        // hidden action to consolidated branch action
        // (i.e. consolidated group continues to another consolidated group)
        if (consolidatedBranchDefaultActions.has(edge.from)) {
          // only add in edges from default action to avoid duplicates
          let group = consolidatedActionsGroups[parentConsolidatedBranchMap[edge.from]];
          consolidatedEdges.push({
            from: group.defaultActionId,
            to: consolidatedActionsGroups[edge.to].defaultActionId,
            type: 'continue',
          });
        } else {
          delete targetIds[edge.from];
        }
        return;
      }

      if (parentConsolidatedBranchMap[edge.to] != null) {
        // edge to hidden action
        return;
      }

      if (parentConsolidatedBranchMap[edge.from] != null && !consolidatedBranchDefaultActions.has(edge.from)) {
        // hidden action that's not a default action
        delete targetIds[edge.from];
        return;
      }

      if (consolidatedActionsGroups[edge.to] != null) {
        // reroute edge so it goes to default action instead of hidden action(s)
        let defaultAction = consolidatedActionsGroups[edge.to].defaultActionId;
        consolidatedEdges.push({
          ...edge,
          to: defaultAction,
        });
        return;
      }

      consolidatedEdges.push(edge);
    });

    // populate group name and action language map
    Object.keys(consolidatedActionsGroups).forEach((key) => {
      let group = consolidatedActionsGroups[key];
      group.name = actionMap[group.defaultActionId].name;

      let parentAction = consolidatedActionMap[key];
      let branchConditions = (parentAction?.multiConditions ?? []) as SerializedLegacyAttributeFilter[][];

      group.branchedActionIds.forEach((a) => {
        let language = '';
        let index = group.edges.find((e) => e.to === a)!.index!;

        if (branchConditions[index]?.length > 0) {
          language = branchConditions[index][0].value!;
        }

        // Language code value may be all lowercase. Example: "en-us" instead of "en-US".
        // Lookup the correct language code.
        group.actionLanguageMap[a] = getLocaleByCode(language)?.code ?? language;
      });
    });
  }

  return {
    actionMap,
    targetIds,
    consolidatedEdges,
    consolidatedActions,
    consolidatedActionsGroups,
    consolidatedBranchDefaultActions,
    parentConsolidatedBranchMap,
  };
}

// Return edges with consolidated actions expanded out
export function expandTree(
  consolidatedActionsGroups: Record<string, ConsolidatedActionsGroup>,
  parentConsolidatedBranchMap: Record<string, string>,
  newEdges: Edge[],
) {
  let expandedEdges: Edge[] = [];

  newEdges.forEach((edge) => {
    if (parentConsolidatedBranchMap[edge.to] != null && parentConsolidatedBranchMap[edge.from] != null) {
      // edge goes from consolidated default action to another consolidated default action
      let fromGroup = consolidatedActionsGroups[parentConsolidatedBranchMap[edge.from]];
      let toGroup = consolidatedActionsGroups[parentConsolidatedBranchMap[edge.to]];

      // add branch edges for first group and set branches to go to second group parent
      fromGroup.edges.forEach((e) => expandedEdges.push(e));
      fromGroup.branchedActionIds.forEach((e) =>
        expandedEdges.push({
          from: e,
          to: toGroup.parentActionId,
          type: 'continue',
        }),
      );

      // add edges for second group
      toGroup.edges.forEach((e) => expandedEdges.push(e));

      return;
    } else if (parentConsolidatedBranchMap[edge.from] != null) {
      // edge goes from a consolidated default action
      let group = consolidatedActionsGroups[parentConsolidatedBranchMap[edge.from]];

      // add in branch actions that were consolidated/hidden
      group.edges.forEach((e) => expandedEdges.push(e));

      // route all related consolidated actions
      group.branchedActionIds.forEach((e) => expandedEdges.push({ from: e, to: edge.to, type: 'continue' }));

      return;
    } else if (parentConsolidatedBranchMap[edge.to] != null) {
      // edge goes to consolidated default action, so route to parent action instead
      let group = consolidatedActionsGroups[parentConsolidatedBranchMap[edge.to]];
      if (edge.from !== group.parentActionId) {
        expandedEdges.push({ ...edge, to: group.parentActionId });
      }

      // add in branch actions that were consolidated/hidden
      group.edges.forEach((e) => expandedEdges.push(e));

      return;
    }

    expandedEdges.push(edge);
  });

  // Filter out non-unique edges.
  let expandedEdgesMap = new Map();
  expandedEdges.forEach((e) => expandedEdgesMap.set(`${e.from}-${e.to}-${e.type}-${e.index ?? ''}`, e));
  return Array.from(expandedEdgesMap.values());
}

export function expandTraversalOrder(
  consolidatedTraversalOrder: string[],
  consolidatedActionsGroups: Record<string, ConsolidatedActionsGroup>,
  parentConsolidatedBranchMap: Record<string, string>,
) {
  let traversalOrder = [...consolidatedTraversalOrder];
  for (let i = traversalOrder.length - 1; i >= 0; i--) {
    let parentActionId = parentConsolidatedBranchMap[traversalOrder[i]];
    if (parentActionId == null) {
      continue;
    }

    let consolidatedGroup = consolidatedActionsGroups[parentActionId];
    traversalOrder.splice(i, 1, ...consolidatedGroup.branchedActionIds, consolidatedGroup.parentActionId);
  }

  return traversalOrder;
}

export function getActionIdsInConsolidatedGroup(
  actionId: string,
  consolidatedActionsGroups: Record<string, ConsolidatedActionsGroup>,
  parentConsolidatedBranchMap: Record<string, string>,
) {
  let parentActionId = parentConsolidatedBranchMap[actionId];
  if (parentActionId != null) {
    let consolidatedGroup = consolidatedActionsGroups[parentActionId];
    return [consolidatedGroup.parentActionId, ...consolidatedGroup.branchedActionIds];
  }

  return [actionId];
}
